import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, getShiftSetting, getShiftSettings, getPlannedShifts, getShiftForDate, getTimeSlotsForShift, isDayAvailableForShift, DEFAULT_SHIFT_SETTINGS, toLocalDateStr } from "../supabase"; // Import Supabase Client
import InstallPWA from "./InstallPWA";
import { sendConfirmationEmail, sendCancellationEmail } from "../emailService";
// import "../AppointmentForm.css"; // Using Tailwind CSS instead


function AppointmentForm({ onLogout }) {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [time, setTime] = useState("");
  const [services, setServices] = useState([]); // Mehrfachauswahl
  const [availableTimes, setAvailableTimes] = useState({ free: [], all: [], booked: [] });
  const [existingAppointment, setExistingAppointment] = useState(null);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [blockedDates, setBlockedDates] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  // Schichtsystem State
  const [currentShift, setCurrentShift] = useState("normal");
  const [shiftSettings, setShiftSettings] = useState(DEFAULT_SHIFT_SETTINGS);
  const [plannedShifts, setPlannedShifts] = useState([]);
  const [isLoadingShift, setIsLoadingShift] = useState(true);

  useEffect(() => {
    const checkAuthentication = async () => {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (!user || authError) {
        setError("Benutzer ist nicht authentifiziert.");
        navigate("/login");
        return;
      }

      try {
        const { data: userProfile, error: profileError } = await supabase
          .from("users")
          .select("username")
          .eq("id", user.id)
          .single();

        if (profileError || !userProfile) {
          setError("Benutzername konnte nicht gefunden werden.");
        } else {
          setUsername(userProfile.username);
        }
      } catch (err) {
        console.error("Fehler beim Abrufen des Benutzernamens:", err);
        setError("Fehler beim Abrufen des Benutzernamens.");
      } finally {
        setLoading(false);
      }
    };

    checkAuthentication();
    fetchBlockedDates();
    loadShiftSetting();

    // Realtime Subscription für Schichtänderungen
    const shiftSubscription = supabase
      .channel('admin_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_settings'
        },
        (payload) => {
          console.log("Admin Settings geändert:", payload);
          if (payload.new) {
            if (payload.new.key === 'current_shift' && payload.new.value) {
              setCurrentShift(payload.new.value);
            }
            if (payload.new.key === 'shift_settings' && payload.new.value) {
              try {
                const settings = typeof payload.new.value === 'string'
                  ? JSON.parse(payload.new.value)
                  : payload.new.value;
                setShiftSettings(prev => ({ ...prev, ...settings }));
              } catch (e) {
                console.error("Fehler beim Parsen der Schichteinstellungen:", e);
              }
            }
            if (payload.new.key === 'planned_shifts') {
              try {
                if (payload.new.value && payload.new.value !== '' && payload.new.value !== '[]') {
                  const planned = typeof payload.new.value === 'string'
                    ? JSON.parse(payload.new.value)
                    : payload.new.value;
                  if (Array.isArray(planned)) {
                    setPlannedShifts(planned);
                  } else {
                    setPlannedShifts([]);
                  }
                } else {
                  setPlannedShifts([]);
                }
              } catch (e) {
                setPlannedShifts([]);
              }
            }
          }
        }
      )
      .subscribe();

    // Cleanup bei Unmount
    return () => {
      supabase.removeChannel(shiftSubscription);
    };
  }, [navigate]);

  // Schichteinstellung laden
  const loadShiftSetting = async () => {
    setIsLoadingShift(true);
    try {
      const { shift, error } = await getShiftSetting();
      if (!error) {
        setCurrentShift(shift);
        console.log("Schicht geladen:", shift);
      }
      // Lade auch die benutzerdefinierten Einstellungen
      const { settings } = await getShiftSettings();
      if (settings) {
        setShiftSettings(settings);
        console.log("Schichteinstellungen geladen:", settings);
      }
      // Lade Schichtplan
      const { plannedShifts: loaded } = await getPlannedShifts();
      setPlannedShifts(loaded || []);
      console.log("Schichtplan geladen:", loaded);
    } catch (err) {
      console.error("Fehler beim Laden der Schichteinstellung:", err);
    } finally {
      setIsLoadingShift(false);
    }
  };

  useEffect(() => {
    if (username && !isLoadingShift) {
      fetchAvailableTimes();
      checkExistingAppointment();
    }
  }, [selectedDate, username, currentShift, plannedShifts, isLoadingShift, services]);

  useEffect(() => {
    function handleClickOutside(event) {
      // Nur schließen wenn Dropdown offen ist und Klick NICHT im Modal
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('[data-time-modal]')
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchBlockedDates = async () => {
    try {
      const { data, error } = await supabase.from("blocked_dates").select("date");
      if (error) throw error;
      setBlockedDates(data.map((entry) => new Date(entry.date)));
    } catch (err) {
      console.error("Fehler beim Abrufen der gesperrten Tage:", err);
      setError("Fehler beim Abrufen der gesperrten Tage.");
    }
  };

  // Noch robustere Hilfsfunktion: Zeit-String in HH:mm normalisieren (auch für Timestamps, Sekunden, Leerzeichen)
  function normalizeTime(t) {
    if (!t) return "";
    // Falls ein Timestamp: nur Uhrzeit extrahieren
    if (t.includes("T")) t = t.split("T")[1];
    // Entferne Sekunden, Millisekunden, Leerzeichen
    t = t.trim().split(".")[0];
    const [h, m] = t.split(":");
    return `${(h || "00").padStart(2, "0")}:${(m || "00").padStart(2, "0")}`;
  }

  // Hilfsfunktion: Dauer je Dienstleistung in Minuten
  function getServiceDuration(service) {
    if (service === "Haare und Bart") return 45;
    if (service === "Bart" || service === "Augenbrauen zupfen") return 15;
    return 30; // Haare
  }

  // Hilfsfunktion: Gesamtdauer aller gewählten Dienstleistungen
  function getTotalDuration(servicesArr) {
    if (!servicesArr || servicesArr.length === 0) return 0;
    let total = 0;
    servicesArr.forEach(service => {
      if (service === "Haare und Bart") total += 45;
      else if (service === "Bart" || service === "Augenbrauen zupfen") total += 15;
      else total += 30; // Haare
    });
    return total;
  }

  const fetchAvailableTimes = async () => {
    try {
      setIsLoadingTimes(true);
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const nextDay = new Date(startOfDay);
      nextDay.setDate(startOfDay.getDate() + 1);
      const { data: appointments, error } = await supabase
        .from("appointments")
        .select("time, service, date")
        .gte("date", startOfDay.toISOString())
        .lt("date", nextDay.toISOString());
      if (error) throw error;
      // Alle gebuchten Slots (inkl. Mehrfach-Block für "Haare und Bart")
      let bookedTimes = [];
      appointments.forEach(appt => {
        const base = normalizeTime(appt.time);
        // Dauer auslesen
        let duration = 30;
        if (appt.service.includes("Haare und Bart")) duration = 45;
        else if (appt.service.includes("Bart") || appt.service.includes("Augenbrauen zupfen")) duration = 15;
        if (appt.service.includes(",")) {
          // Kommagetrennte Liste: Summe der Einzelzeiten
          duration = appt.service.split(",").map(s => s.trim()).reduce((sum, s) => {
            if (s === "Haare und Bart") return sum + 45;
            if (s === "Bart" || s === "Augenbrauen zupfen") return sum + 15;
            return sum + 30;
          }, 0);
        }
        // Blockiere alle Slots, die von diesem Termin belegt werden
        let [h, m] = base.split(":").map(Number);
        let minutes = duration;
        while (minutes > 0) {
          const slot = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
          bookedTimes.push(slot);
          m += 30;
          if (m >= 60) { h += 1; m -= 60; }
          minutes -= 30;
        }
      });
      // Filtere freie Slots je nach Gesamtdauer
      const allSlots = generateTimeSlots().map(normalizeTime);
      const totalDuration = getTotalDuration(services);
      const blocksNeeded = Math.ceil(totalDuration / 30);
      let freeSlots = allSlots.filter((slot, idx, arr) => {
        if (!services.length) return false;
        // Prüfe, ob ab diesem Slot genug freie Blöcke am Stück sind
        let [h, m] = slot.split(":").map(Number);
        let ok = true;
        for (let i = 0; i < blocksNeeded; i++) {
          const checkSlot = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
          if (bookedTimes.includes(checkSlot) || !arr.includes(checkSlot)) {
            ok = false;
            break;
          }
          m += 30;
          if (m >= 60) { h += 1; m -= 60; }
        }
        return ok;
      });
      setAvailableTimes({
        free: freeSlots,
        all: allSlots,
        booked: bookedTimes,
      });
      setIsLoadingTimes(false);
    } catch (err) {
      setIsLoadingTimes(false);
      setError("Fehler beim Laden der verfügbaren Zeiten.");
    }
  };

  // Generiert Zeitslots basierend auf der Schicht für das ausgewählte Datum
  const generateTimeSlots = () => {
    // Ermittle welche Schicht für dieses Datum gilt
    const effectiveShift = getEffectiveShift(selectedDate);
    return getTimeSlotsForShift(effectiveShift, shiftSettings, selectedDate);
  };

  const checkExistingAppointment = async () => {
    if (!username) return;

    try {
      const { data: appointment, error } = await supabase
        .from("appointments")
        .select("*")
        .eq("name", username)
        .maybeSingle();

      if (error) {
        throw error;
      }

      setExistingAppointment(appointment || null);
    } catch (err) {
      console.error("Fehler beim Überprüfen des bestehenden Termins:", err);
      setError("Fehler beim Überprüfen des bestehenden Termins.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const userResponse = await supabase.auth.getUser();
    const user = userResponse.data?.user;

    if (!user) {
      setError("Benutzer ist nicht authentifiziert. Bitte melden Sie sich erneut an.");
      return;
    }

    if (!username) {
      setError("Benutzername konnte nicht geladen werden. Bitte laden Sie die Seite neu.");
      return;
    }

    if (!services.length) {
      setError("Bitte wählen Sie mindestens eine Dienstleistung aus.");
      return;
    }

    if (!time) {
      setError("Bitte wählen Sie eine Zeit aus.");
      return;
    }

    try {
      const selectedDateStr = selectedDate.toLocaleDateString("en-CA"); // Format: YYYY-MM-DD

      // Überprüfen, ob das Datum blockiert ist
      const isBlocked = blockedDates.some(
        (blockedDate) => blockedDate.toLocaleDateString("en-CA") === selectedDateStr
      );

      if (isBlocked) {
        setError("Dieser Tag ist blockiert. Bitte wählen Sie ein anderes Datum.");
        return;
      }

      const selectedDateTime = new Date(selectedDate);
      const [hours, minutes] = time.split(":").map(Number);
      selectedDateTime.setHours(hours, minutes, 0, 0);

      // Ein Eintrag für alle Dienstleistungen zusammen
      const serviceString = services.join(", ");
      const insertRow = {
        user_id: user.id,
        name: username,
        time,
        service: serviceString,
        date: selectedDateTime.toISOString(),
      };
      const { error } = await supabase.from("appointments").insert([insertRow]);

      if (error) {
        if (error.code === "23505" || error.code === "P0001") {
          setError("Doppelbuchung nicht möglich. Diese Zeit ist bereits belegt.");
        } else {
          throw error;
        }
      } else {
        setTime("");
        setServices([]);
        setSuccess("Termin erfolgreich gebucht!");
        checkExistingAppointment();

        // Bestätigungs-Email an Kunden senden
        if (user.email) {
          const appointmentData = {
            date: selectedDateTime.toISOString(),
            time: time,
            service: serviceString,
          };
          sendConfirmationEmail(user.email, appointmentData, username)
            .then(() => console.log("📧 Bestätigungs-Email gesendet"))
            .catch((err) => console.error("Email-Fehler:", err));
        }
      }
    } catch (err) {
      console.error("Fehler beim Buchen des Termins:", err);
      setError("Fehler beim Buchen des Termins. Bitte versuchen Sie es erneut.");
    }
  };


  const handleDeleteAppointment = async () => {
    if (!existingAppointment) return;

    // Speichere Termin-Daten vor dem Löschen für die Email
    const appointmentToCancel = { ...existingAppointment };

    try {
      const { error } = await supabase
        .from("appointments")
        .delete()
        .eq("id", existingAppointment.id);

      if (error) throw error;

      setExistingAppointment(null);
      setSuccess("Ihr bestehender Termin wurde erfolgreich gelöscht.");

      // Stornierung-Email an Kunden senden
      const userResponse = await supabase.auth.getUser();
      const user = userResponse.data?.user;
      if (user?.email) {
        sendCancellationEmail(user.email, appointmentToCancel, username, false)
          .then(() => console.log("📧 Stornierung-Email gesendet"))
          .catch((err) => console.error("Email-Fehler:", err));
      }
    } catch (err) {
      console.error("Fehler beim Löschen des Termins:", err);
      setError("Fehler beim Löschen des Termins. Bitte versuchen Sie es erneut.");
    }
  };

  const handleDateChange = (date) => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);
    if (isWeekday(date) && date <= maxDate) {
      setSelectedDate(date);
      setSuccess("");
      setError("");
    } else if (!isWeekday(date)) {
      // Schicht-spezifische Fehlermeldung — auf Basis der für DIESES Datum gültigen Schicht,
      // nicht der aktuell laufenden (Datum kann unter eine geplante Schicht fallen).
      const effectiveShift = getEffectiveShift(date);
      if (effectiveShift === "spaetschicht") {
        setError("Bei Spätschicht sind nur Wochenenden (Samstag und Sonntag) verfügbar.");
      } else if (effectiveShift === "nachtschicht") {
        setError("Bei Nachtschicht sind aktuell keine Termine möglich.");
      } else {
        setError("Bitte wählen Sie einen verfügbaren Tag.");
      }
    } else {
      setError("Sie können nur eine Woche im Voraus buchen.");
    }
  };

  // Ermittelt welche Schicht für ein bestimmtes Datum gilt
  const getEffectiveShift = (date) => {
    return getShiftForDate(date, currentShift, plannedShifts);
  };

  // Prüft ob ein Tag für die aktuelle/geplante Schicht verfügbar ist
  const isWeekday = (date) => {
    const effectiveShift = getEffectiveShift(date);
    return isDayAvailableForShift(date, effectiveShift, shiftSettings);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onLogout();
    navigate("/login");
  };

  return (
    <div className="bg-app min-h-screen p-4">

      {/* === MODALS (ausserhalb des Layouts fuer korrektes Overlay) === */}
      {serviceModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-lg p-6 sm:p-8" onClick={() => setServiceModalOpen(false)}>
          <div className="bg-zinc-800 border-2 border-gold-500/40 rounded-2xl shadow-[0_0_80px_rgba(251,191,36,0.15),0_25px_50px_rgba(0,0,0,0.5)] max-w-sm sm:max-w-md w-full p-6 sm:p-8 relative animate-scale-in max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg sm:text-xl font-bold text-zinc-100 mb-6 text-center">Dienstleistungen waehlen</h3>
            <div className="flex flex-col gap-4">
              {[
                { label: "Haare", value: "Haare" },
                { label: "Bart", value: "Bart" },
                { label: "Haare und Bart", value: "Haare und Bart" },
                { label: "Augenbrauen zupfen", value: "Augenbrauen zupfen" },
              ].map(opt => {
                let disabled = false;
                if (opt.value === "Haare und Bart" && (services.includes("Haare") || services.includes("Bart"))) disabled = true;
                if ((opt.value === "Haare" || opt.value === "Bart") && services.includes("Haare und Bart")) disabled = true;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      if (services.includes(opt.value)) {
                        setServices(services.filter(s => s !== opt.value));
                      } else {
                        setServices([...services, opt.value]);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-300 text-base font-medium focus:outline-none
                      ${services.includes(opt.value)
                        ? 'bg-gold-600/20 border-gold-500/40 text-gold-300 ring-2 ring-gold-500/30'
                        : disabled
                          ? 'bg-zinc-900 border-zinc-700 text-zinc-500 opacity-60 cursor-not-allowed'
                          : 'bg-zinc-900 border-zinc-600 text-zinc-200 hover:border-gold-500/30 hover:bg-zinc-800'}
                    `}
                  >
                    <span>{opt.label}</span>
                    {services.includes(opt.value) && !disabled && (
                      <span className="text-gold-300">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <button className="absolute top-4 right-4 text-zinc-400 hover:text-gold-400 p-1.5 transition-all duration-300 hover:bg-zinc-700/50 rounded-lg" onClick={() => setServiceModalOpen(false)} aria-label="Schliessen">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      {dropdownOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-lg p-6 sm:p-8" onClick={() => setDropdownOpen(false)}>
          <div data-time-modal className="bg-zinc-800 border-2 border-gold-500/40 rounded-2xl shadow-[0_0_80px_rgba(251,191,36,0.15),0_25px_50px_rgba(0,0,0,0.5)] max-w-sm sm:max-w-md w-full p-5 sm:p-6 relative animate-scale-in max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg sm:text-xl font-bold text-zinc-100 mb-4 text-center shrink-0">Uhrzeit waehlen</h3>
            {isLoadingTimes ? (
              <div className="text-center text-zinc-400 py-8">Lade verfuegbare Zeiten...</div>
            ) : (
              <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 px-1 py-2">
                {availableTimes.all.map((slot, idx, arr) => {
                  const totalDuration = getTotalDuration(services);
                  const blocksNeeded = Math.ceil(totalDuration / 30);
                  let [h, m] = slot.split(":").map(Number);
                  let ok = true;
                  let tempH = h, tempM = m;
                  for (let i = 0; i < blocksNeeded; i++) {
                    const checkSlot = `${tempH.toString().padStart(2, "0")}:${tempM.toString().padStart(2, "0")}`;
                    if (!arr.includes(checkSlot) || availableTimes.booked.includes(checkSlot)) ok = false;
                    tempM += 30;
                    if (tempM >= 60) { tempH += 1; tempM -= 60; }
                  }
                  const isSelected = slot === time && ok;
                  let highlight = false;
                  if (time && ok) {
                    let [selH, selM] = time.split(":").map(Number);
                    for (let i = 0; i < blocksNeeded; i++) {
                      const checkSlot = `${selH.toString().padStart(2, "0")}:${selM.toString().padStart(2, "0")}`;
                      if (slot === checkSlot) highlight = true;
                      selM += 30;
                      if (selM >= 60) { selH += 1; selM -= 60; }
                    }
                  }
                  return (
                    <button
                      key={slot}
                      type="button"
                      disabled={!ok}
                      onClick={() => { if (ok) { setTime(slot); setDropdownOpen(false); } }}
                      className={`w-full py-3 rounded-xl border-2 flex items-center justify-center text-base font-semibold transition-all duration-300 relative
                        ${ok
                          ? (isSelected
                            ? 'border-gold-500 bg-gradient-to-r from-gold-600 to-gold-700 text-white shadow-lg'
                            : highlight
                              ? 'border-gold-400 bg-gold-500/20 text-gold-300 font-bold'
                              : 'border-zinc-600 bg-zinc-900 text-zinc-200 hover:bg-gold-500/15 hover:border-gold-500/30 cursor-pointer')
                          : 'border-red-500/50 bg-red-900/20 text-red-400 opacity-60 cursor-not-allowed'}
                      `}
                      style={{ minHeight: '48px' }}
                    >
                      {slot}
                      {isSelected && ok && (
                        <span className="absolute right-3 text-white">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <button className="absolute top-4 right-4 text-zinc-400 hover:text-gold-400 p-1.5 transition-all duration-300 hover:bg-zinc-700/50 rounded-lg" onClick={() => setDropdownOpen(false)} aria-label="Schliessen">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-6xl 2xl:max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-6 mb-6 relative animate-fade-in">
          <div className="flex justify-between items-center">
            <div className="text-center flex-1">
              <h2 className="text-3xl font-bold text-zinc-100 mb-2">Termin buchen</h2>
              <p className="text-zinc-400">Willkommen, {username}</p>
            </div>
            <div className="flex items-center gap-3">
              <InstallPWA />
              <button
                onClick={handleLogout}
                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg flex items-start space-x-3">
            <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-green-300 text-sm">{success}</p>
          </div>
        )}

        {/* Kleines Badge für verfügbare Zeiten */}
        {!loading && !isLoadingShift && currentShift !== "normal" && currentShift !== "nachtschicht" && shiftSettings[currentShift]?.schedule && (
          <div className="mb-4 flex justify-center">
            {(() => {
              const daySchedule = shiftSettings[currentShift]?.schedule?.[selectedDate.getDay()];
              if (!daySchedule) return null;
              return (
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                  currentShift === "spaetschicht"
                    ? "bg-blue-900/30 text-blue-300 border border-blue-500/40"
                    : "bg-amber-900/30 text-amber-300 border border-amber-500/40"
                }`}>
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {daySchedule.start}-{daySchedule.end}
                </span>
              );
            })()}
          </div>
        )}

        {loading || isLoadingShift ? (
          <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-8 text-center">
            <div className="flex items-center justify-center space-x-3">
              <svg className="animate-spin h-6 w-6 text-zinc-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-zinc-300">Lade Daten...</p>
            </div>
          </div>
        ) : currentShift === "nachtschicht" ? (
          <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-8">
            <div className="text-center">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-base font-medium bg-red-900/30 text-red-300 border border-red-500/40 mb-4">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Aktuell keine Termine
              </span>
              <p className="text-zinc-400 text-sm">Bitte später erneut versuchen.</p>
            </div>
          </div>
        ) : existingAppointment ? (
          <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900/20 border border-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-zinc-100 mb-2">Bestehender Termin</h3>
              <p className="text-zinc-300 mb-6">
                Sie haben einen Termin am{" "}
                <span className="font-semibold text-gold-300">
                  {new Date(existingAppointment.date).toLocaleDateString()}
                </span>{" "}
                um{" "}
                <span className="font-semibold text-gold-300">
                  {existingAppointment.time}
                </span>
              </p>
              <button
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center space-x-2 mx-auto"
                onClick={handleDeleteAppointment}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Termin löschen</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col xl:flex-row gap-6 animate-slide-up">
            {/* Calendar Section */}
            <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-0 flex-1 min-w-0 overflow-hidden">
              <h3 className="text-xl font-semibold text-zinc-100 mb-6 text-center p-4">Datum wählen</h3>
              {/* Clean Minimalist Calendar */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-0 w-full">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-5 p-4">
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    className="p-2 hover:bg-gold-500/15 rounded-lg transition-all duration-300"
                  >
                    <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <h3 className="text-lg font-semibold text-zinc-100">
                    {currentMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                  </h3>

                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    className="p-2 hover:bg-gold-500/15 rounded-lg transition-all duration-300"
                  >
                    <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 mb-4 gap-0 pl-0 pr-0">
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
                    <div key={day} className="text-center text-xs font-semibold text-gold-400/80 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-0 pl-0 pr-0">
                  {(() => {
                    const year = currentMonth.getFullYear();
                    const month = currentMonth.getMonth();
                    const firstDay = new Date(year, month, 1);
                    const startDate = new Date(firstDay);
                    startDate.setDate(startDate.getDate() - (firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1));

                    const days = [];
                    const today = new Date();
                    const maxDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

                    for (let i = 0; i < 42; i++) {
                      const date = new Date(startDate);
                      date.setDate(startDate.getDate() + i);

const isCurrentMonth = date.getMonth() === month;
                                      const isToday = date.toDateString() === today.toDateString();
                                      const isSelected = date.toDateString() === selectedDate.toDateString();
                                      // Prüfe ob Tag für die Schicht an diesem Datum verfügbar ist
                                      const effectiveShift = getShiftForDate(date, currentShift, plannedShifts);
                                      const isDayAvailable = isDayAvailableForShift(date, effectiveShift, shiftSettings);
                                      const dateStr = toLocalDateStr(date);
                                      const isBlocked = blockedDates.some((blockedDate) =>
                                        toLocalDateStr(blockedDate) === dateStr
                                      );
                                      const isPast = date < today && !isToday;
                                      const isTooFar = date > maxDate;
                                      const isUnavailable = !isDayAvailable || isBlocked || isPast || isTooFar;

                                      let className = "w-full h-10 text-sm transition-all duration-300 font-medium text-center rounded-lg ";

                                      // Übergangstage (Vor-/Folgemonat) zeigen ihren echten Status an,
                                      // werden aber etwas dezenter dargestellt als Tage des aktuellen Monats.
                                      if (isUnavailable) {
                                        className += (isCurrentMonth ? "text-zinc-500" : "text-zinc-700") + " cursor-not-allowed ";
                                      } else if (isSelected) {
                                        className += "bg-gradient-to-r from-gold-600 to-gold-700 text-white font-bold shadow-lg shadow-gold-600/20 ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-105 ";
                                      } else if (isToday) {
                                        className += "bg-zinc-700 text-zinc-100 ring-1 ring-gold-500/30 ";
                                      } else {
                                        className += (isCurrentMonth ? "text-zinc-300" : "text-zinc-400") + " hover:bg-gold-500/15 hover:text-white hover:scale-105 cursor-pointer ";
                                      }

                                      days.push(
                                        <button
                                          key={i}
                                          className={className}
                                          onClick={() => {
                                            if (isUnavailable) return;
                                            // Bei einem Tag aus dem Folge-/Vormonat zuerst in dessen Monat springen
                                            if (!isCurrentMonth) {
                                              setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                                            }
                                            setSelectedDate(date);
                                          }}
                                          disabled={isUnavailable}
                                        >
                                          {date.getDate()}
                                        </button>
                                      );
                    }

                    return days;
                  })()}
                </div>
              </div>
            </div>

            {/* Form Section */}
            <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-6 w-full xl:w-80 2xl:w-96 shrink-0">
              <h3 className="text-xl font-semibold text-zinc-100 mb-6 text-center">Termin buchen</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dienstleistungs-Auswahl */}
                <div className="w-full">
                  <label className="block text-sm font-medium text-zinc-300 mb-3">
                    Dienstleistungen
                  </label>
                  <button
                    type="button"
                    className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-100 focus:border-gold-500/50 focus:ring-2 focus:ring-gold-500/20 flex justify-between items-center cursor-pointer transition-all duration-300 hover:border-zinc-500"
                    onClick={() => setServiceModalOpen(true)}
                  >
                    <span className={services.length ? "text-zinc-100" : "text-zinc-500"}>
                      {services.length ? services.join(", ") : 'Dienstleistungen wählen'}
                    </span>
                    <svg className="w-5 h-5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
              {error && !services.length && <span className="text-red-400 text-xs mt-1">Bitte wählen Sie mindestens eine Dienstleistung aus.</span>}
            </div>
                {/* Zeit-Auswahl */}
                {services.length > 0 && (
                  <div className="w-full">
                    <label htmlFor="time-dropdown" className="block text-sm font-medium text-zinc-300 mb-3">
                      Uhrzeit
                    </label>
                    <div className="relative w-full" ref={dropdownRef}>
                      <button
                        type="button"
                        id="time-dropdown"
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-100 focus:border-gold-500/50 focus:ring-2 focus:ring-gold-500/20 flex justify-between items-center cursor-pointer transition-all duration-300 hover:border-zinc-500"
                        onClick={() => {
                          fetchAvailableTimes();
                          setDropdownOpen(true);
                        }}
                      >
                        <span className={time ? "text-zinc-100" : "text-zinc-500"}>{time || 'Zeit wählen'}</span>
                        <svg className="w-5 h-5 text-zinc-500 transition-transform duration-200" style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                </div>
              </div>
            )}
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-gold-600 to-gold-700 hover:from-gold-500 hover:to-gold-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Termin buchen</span>
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AppointmentForm;
